/**
 * Types for the supplier bulk location edit flow, mirroring the backend
 * `BulkLocationEditPreviewVO` / `BulkLocationEditApplyResultVO`.
 */

/** A single field-level change, rendered as "old -> new" in the preview. */
export interface BulkLocationFieldChange {
  field: string;
  oldValue: string | null;
  newValue: string | null;
  /** Set when the change is allowed but has consequences worth confirming. */
  warning: string | null;
}

/** A problem found while validating the upload, tied to a row and optionally a cell. */
export interface BulkLocationEditIssue {
  /** 1-based line number in the uploaded file, matching what the spreadsheet shows. */
  row: number;
  locationId: string | null;
  field: string | null;
  message: string;
}

/** The proposed outcome for one location. */
export interface BulkLocationEditRowPreview {
  row: number;
  locationId: string | null;
  locationName: string | null;
  changes: BulkLocationFieldChange[];
  errors: BulkLocationEditIssue[];
  valid: boolean;
}

/**
 * Whether a row would actually change anything.
 *
 * The backend exposes this as `hasChanges()`, which Jackson does not serialize
 * (it only detects `get`/`is` prefixes), so the field is absent from the JSON
 * and must be derived here rather than read off the response.
 */
export const rowHasChanges = (row: BulkLocationEditRowPreview): boolean =>
  row.changes.length > 0;

/** Result of the dry run. Nothing has been saved when this is returned. */
export interface BulkLocationEditPreview {
  rowsProcessed: number;
  rowsValid: number;
  locationsWithChanges: number;
  fieldsChanged: number;
  rows: BulkLocationEditRowPreview[];
  /** Problems not tied to one row, e.g. an unreadable file or a missing header. */
  fileErrors: BulkLocationEditIssue[];
  warnings: string[];
  ignoredColumns: string[];
  /** True when every row is clean and the file can be applied. */
  applyable: boolean;
}

/** Result of applying. All-or-nothing: `applied` is false when any row failed. */
export interface BulkLocationEditApplyResult {
  applied: boolean;
  locationsUpdated: number;
  preview: BulkLocationEditPreview;
}
